import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DrunkButton } from '../components/DrunkButton';
import { DrunkCard } from '../components/DrunkCard';
import { useSession } from '../context/SessionContext';
import { ArrowLeft, Clock, Lightbulb, Users, MessageSquare, Coffee, Camera, X, Coins, Trophy, Gift, Heart, Star, MapPin, Sparkles } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Mode-specific theme configurations
const MODE_THEMES = {
  drunk_ai: {
    bg: 'bg-cream',
    accent: 'text-pink',
    cardVariant: 'yellow',
    headerIcon: '🍺',
    headerText: 'Good Morning, Party Animal! ☀️',
    subText: "Here's what your drunk self got up to",
    participantEmoji: '🍺',
    galleryTitle: 'Hangover Gallery 📸',
    footerText: 'Start New Party 🎉'
  },
  party: {
    bg: 'bg-gradient-to-b from-orange-50 to-orange-100',
    accent: 'text-orange-500',
    cardVariant: 'default',
    headerIcon: '🎉',
    headerText: 'Party Recap! 🎊',
    subText: "All the highlights from last night",
    participantEmoji: '🥳',
    galleryTitle: 'Party Pics 📸',
    footerText: 'Start Another Party 🎉'
  },
  keepsake: {
    bg: 'bg-gradient-to-b from-rose-50 to-pink-100',
    accent: 'text-rose-500',
    cardVariant: 'default',
    headerIcon: '💝',
    headerText: 'Your Beautiful Keepsake 💕',
    subText: "Treasured moments to remember forever",
    participantEmoji: '💗',
    galleryTitle: 'Precious Moments 📸',
    footerText: 'Create Another Keepsake 💝'
  },
  roadtrip: {
    bg: 'bg-gradient-to-b from-emerald-50 to-sky-100',
    accent: 'text-emerald-500',
    cardVariant: 'default',
    headerIcon: '🚗',
    headerText: 'Journey Complete! 🗺️',
    subText: "Your adventure, captured",
    participantEmoji: '🧳',
    galleryTitle: 'Road Trip Memories 📸',
    footerText: 'Plan Next Adventure 🚗'
  },
  event: {
    bg: 'bg-gradient-to-b from-slate-50 to-indigo-100',
    accent: 'text-indigo-500',
    cardVariant: 'default',
    headerIcon: '📋',
    headerText: 'Event Summary 📊',
    subText: "Key moments documented",
    participantEmoji: '✅',
    galleryTitle: 'Event Photos 📸',
    footerText: 'Log Another Event 📋'
  }
};

export const Summary = () => {
  const { sessionCode } = useParams();
  const navigate = useNavigate();
  const { session, clearSession } = useSession();
  
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPhoto, setSelectedPhoto] = useState(null);

  useEffect(() => {
    const loadSummary = async () => {
      if (!session || session.session_code !== sessionCode.toUpperCase()) {
        navigate(`/session/${sessionCode}`);
        return;
      }

      try {
        const response = await axios.get(
          `${API}/sessions/${sessionCode}/summary?pin=${session.pin}`
        );
        setSummary(response.data);
      } catch (error) {
        console.error('Failed to load summary:', error);
        const message = error.response?.data?.detail || "Couldn't load summary";
        toast.error(message);
        if (error.response?.status === 401 || error.response?.status === 404) {
          clearSession();
          navigate('/');
        }
      } finally {
        setIsLoading(false);
      }
    };

    loadSummary();
  }, [sessionCode, session, navigate, clearSession]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4 animate-pulse">✨</div>
          <p className="font-heading text-2xl">Creating your summary...</p>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">😵</div>
          <p className="font-heading text-2xl">No summary found</p>
        </div>
      </div>
    );
  }

  const mode = summary.mode || 'drunk_ai';
  const theme = MODE_THEMES[mode] || MODE_THEMES.drunk_ai;
  const modeConfig = summary.mode_config || {};

  return (
    <div className={`min-h-screen ${theme.bg} p-4`}>
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <DrunkButton
            variant="ghost"
            onClick={() => navigate(`/session/${sessionCode}`)}
            data-testid="back-to-chat-btn"
            className="text-sm"
          >
            <ArrowLeft className="w-4 h-4 inline mr-1" />
            Back
          </DrunkButton>
          
          <DrunkButton
            variant="secondary"
            size="small"
            onClick={() => navigate('/')}
            data-testid="new-session-btn"
            className="text-sm"
          >
            New Session
          </DrunkButton>
        </div>

        {/* Mode-Themed Header */}
        <div className="text-center mb-8 bounce-in">
          <div className="text-5xl mb-3">{theme.headerIcon}</div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2">
            {theme.headerText}
          </h1>
          <p className="font-body text-base text-black/70">
            {theme.subText}
          </p>
          <p className="font-mono font-bold text-sm mt-2 bg-white/50 inline-block px-3 py-1 rounded-full">
            {sessionCode}
          </p>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-2 gap-3 mb-6">
          <DrunkCard variant="cyan" className="text-center p-3">
            <MessageSquare className="w-6 h-6 mx-auto mb-1" />
            <p className="font-heading text-2xl font-bold">{summary.total_messages}</p>
            <p className="font-body text-xs">Messages</p>
          </DrunkCard>
          
          <DrunkCard variant="pink" className="text-center p-3 text-white">
            <Camera className="w-6 h-6 mx-auto mb-1" />
            <p className="font-heading text-2xl font-bold">{summary.photos?.length || 0}</p>
            <p className="font-body text-xs">Photos</p>
          </DrunkCard>
          
          {mode === 'keepsake' && (
            <>
              <DrunkCard variant="yellow" className="text-center p-3">
                <Gift className="w-6 h-6 mx-auto mb-1" />
                <p className="font-heading text-2xl font-bold">{summary.gifts?.length || 0}</p>
                <p className="font-body text-xs">Gifts</p>
              </DrunkCard>
              
              <DrunkCard variant="default" className="text-center p-3">
                <Heart className="w-6 h-6 mx-auto mb-1 text-rose-500" />
                <p className="font-heading text-2xl font-bold">{summary.wishes?.length || 0}</p>
                <p className="font-body text-xs">Wishes</p>
              </DrunkCard>
            </>
          )}
          
          {mode === 'drunk_ai' && (
            <>
              <DrunkCard variant="yellow" className="text-center p-3">
                <Lightbulb className="w-6 h-6 mx-auto mb-1" />
                <p className="font-heading text-2xl font-bold">{summary.big_ideas?.length || 0}</p>
                <p className="font-body text-xs">Ideas</p>
              </DrunkCard>
              
              <DrunkCard variant="default" className="text-center p-3">
                <Users className="w-6 h-6 mx-auto mb-1" />
                <p className="font-heading text-2xl font-bold">{summary.participants?.length || 0}</p>
                <p className="font-body text-xs">People</p>
              </DrunkCard>
            </>
          )}
          
          {(mode === 'party' || mode === 'event') && (
            <>
              <DrunkCard variant="yellow" className="text-center p-3">
                <Star className="w-6 h-6 mx-auto mb-1" />
                <p className="font-heading text-2xl font-bold">{summary.highlights?.length || 0}</p>
                <p className="font-body text-xs">Highlights</p>
              </DrunkCard>
              
              <DrunkCard variant="default" className="text-center p-3">
                <Users className="w-6 h-6 mx-auto mb-1" />
                <p className="font-heading text-2xl font-bold">{summary.participants?.length || 0}</p>
                <p className="font-body text-xs">People</p>
              </DrunkCard>
            </>
          )}
          
          {mode === 'roadtrip' && (
            <>
              <DrunkCard variant="yellow" className="text-center p-3">
                <MapPin className="w-6 h-6 mx-auto mb-1" />
                <p className="font-heading text-2xl font-bold">{summary.stops?.length || 0}</p>
                <p className="font-body text-xs">Stops</p>
              </DrunkCard>
              
              <DrunkCard variant="default" className="text-center p-3">
                <Users className="w-6 h-6 mx-auto mb-1" />
                <p className="font-heading text-2xl font-bold">{summary.participants?.length || 0}</p>
                <p className="font-body text-xs">Travelers</p>
              </DrunkCard>
            </>
          )}
        </div>

        {/* Chaos/Vibe Meter */}
        {summary.chaos_score !== undefined && (
          <DrunkCard 
            variant="default" 
            className="mb-6 p-4 bg-white"
            data-testid="chaos-meter"
          >
            <h2 className="font-heading text-base font-bold mb-3 text-center">
              {mode === 'drunk_ai' ? '🍺 Drunk-O-Meter' : 
               mode === 'keepsake' ? '💝 Love Meter' :
               mode === 'roadtrip' ? '🚗 Adventure Meter' :
               mode === 'party' ? '🎉 Party Meter' : '📊 Vibe Meter'}
            </h2>
            
            {/* Meter Bar */}
            <div className="relative h-8 bg-gray-200 rounded-full overflow-hidden border-2 border-black mb-2">
              <div 
                className={`absolute left-0 top-0 h-full transition-all duration-1000 ease-out ${
                  mode === 'drunk_ai' ? 'bg-gradient-to-r from-green-400 via-yellow-400 via-orange-400 to-red-500' :
                  mode === 'keepsake' ? 'bg-gradient-to-r from-pink-300 via-rose-400 to-red-500' :
                  mode === 'roadtrip' ? 'bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-500' :
                  mode === 'party' ? 'bg-gradient-to-r from-purple-400 via-pink-500 to-orange-500' :
                  'bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-500'
                }`}
                style={{ width: `${summary.chaos_score}%` }}
              />
              {/* Tick marks */}
              <div className="absolute inset-0 flex justify-between px-1">
                {[0, 25, 50, 75, 100].map((tick) => (
                  <div key={tick} className="w-0.5 h-full bg-black/20" />
                ))}
              </div>
            </div>
            
            {/* Score and Level */}
            <div className="flex justify-between items-center">
              <span className="font-mono text-sm font-bold">{summary.chaos_score}%</span>
              <span className="font-heading text-base font-bold">{summary.chaos_level}</span>
            </div>
          </DrunkCard>
        )}

        {/* AI Verdict - Mode themed */}
        <DrunkCard 
          variant={mode === 'keepsake' ? 'default' : 'gradient'} 
          className={`mb-6 p-4 ${mode === 'keepsake' ? 'bg-gradient-to-r from-rose-200 to-pink-200 border-rose-300' : 'text-white'}`}
          data-testid="ai-verdict-card"
        >
          <h2 className={`font-heading text-lg font-bold mb-2 flex items-center gap-2 ${mode === 'keepsake' ? 'text-rose-700' : ''}`}>
            {modeConfig.emoji || '🤖'} {modeConfig.name || 'AI'} Says...
          </h2>
          <p className={`font-body text-sm leading-relaxed ${mode === 'keepsake' ? 'text-rose-800' : ''}`}>
            {summary.grok_verdict}
          </p>
        </DrunkCard>

        <div className="squiggly-divider" />

        {/* GIFTS SECTION - Keepsake Mode */}
        {mode === 'keepsake' && summary.gifts && summary.gifts.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
              <Gift className="w-6 h-6 text-rose-500" />
              Gifts Received 🎁
            </h2>
            
            <div className="grid grid-cols-1 gap-3">
              {summary.gifts.map((gift, index) => (
                <DrunkCard 
                  key={index} 
                  variant="default"
                  className="p-4 bg-white border-rose-200 slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`gift-card-${index}`}
                >
                  <div className="flex items-start gap-3">
                    {gift.photo_url ? (
                      <img 
                        src={gift.photo_url} 
                        alt={gift.item}
                        className="w-16 h-16 object-cover rounded-lg border-2 border-rose-200 cursor-pointer"
                        onClick={() => setSelectedPhoto({ url: gift.photo_url, caption: gift.item, author: gift.giver })}
                      />
                    ) : (
                      <div className="w-16 h-16 bg-rose-100 rounded-lg border-2 border-rose-200 flex items-center justify-center">
                        <Gift className="w-8 h-8 text-rose-400" />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="font-heading font-bold text-base text-rose-700">{gift.item}</p>
                      <p className="font-body text-sm text-rose-500">From {gift.giver}</p>
                    </div>
                  </div>
                </DrunkCard>
              ))}
            </div>
          </div>
        )}

        {/* WISHES SECTION - Keepsake Mode */}
        {mode === 'keepsake' && summary.wishes && summary.wishes.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
              <Heart className="w-6 h-6 text-rose-500" />
              Heartfelt Wishes 💕
            </h2>
            
            <div className="space-y-3">
              {summary.wishes.map((wish, index) => (
                <DrunkCard 
                  key={index} 
                  variant="default"
                  className="p-4 bg-gradient-to-r from-rose-50 to-pink-50 border-rose-200 slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`wish-card-${index}`}
                >
                  <p className="font-body text-sm text-rose-700 italic mb-2">&ldquo;{wish.content}&rdquo;</p>
                  <p className="font-heading font-bold text-xs text-rose-500">— {wish.author}</p>
                </DrunkCard>
              ))}
            </div>
          </div>
        )}

        {/* ADVICE SECTION - Keepsake Mode */}
        {mode === 'keepsake' && summary.advice && summary.advice.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-purple-500" />
              Words of Wisdom ✨
            </h2>
            
            <div className="space-y-3">
              {summary.advice.map((adv, index) => (
                <DrunkCard 
                  key={index} 
                  variant="default"
                  className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200 slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`advice-card-${index}`}
                >
                  <p className="font-body text-sm text-purple-700 mb-2">{adv.content}</p>
                  <p className="font-heading font-bold text-xs text-purple-500">— {adv.author}</p>
                </DrunkCard>
              ))}
            </div>
          </div>
        )}

        {/* STOPS SECTION - Road Trip Mode */}
        {mode === 'roadtrip' && summary.stops && summary.stops.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
              <MapPin className="w-6 h-6 text-emerald-500" />
              Journey Stops 📍
            </h2>
            
            <div className="space-y-3">
              {summary.stops.map((stop, index) => (
                <DrunkCard 
                  key={index} 
                  variant="default"
                  className="p-4 bg-gradient-to-r from-emerald-50 to-sky-50 border-emerald-200 slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`stop-card-${index}`}
                >
                  <p className="font-heading font-bold text-base text-emerald-700">{stop.location}</p>
                  <p className="font-body text-sm text-emerald-600">{stop.description}</p>
                </DrunkCard>
              ))}
            </div>
          </div>
        )}

        {/* HIGHLIGHTS SECTION - Party/Event Mode */}
        {(mode === 'party' || mode === 'event') && summary.highlights && summary.highlights.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
              <Star className="w-6 h-6 text-yellow-500" />
              Top Highlights ⭐
            </h2>
            
            <div className="space-y-3">
              {summary.highlights.map((highlight, index) => (
                <DrunkCard 
                  key={index} 
                  variant="yellow"
                  rotate
                  className="p-4 slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`highlight-card-${index}`}
                >
                  <p className="font-body text-sm mb-2">{highlight.content}</p>
                  <p className="font-heading font-bold text-xs opacity-70">— {highlight.author}</p>
                </DrunkCard>
              ))}
            </div>
          </div>
        )}

        {/* Big Ideas - Drunk AI Mode */}
        {mode === 'drunk_ai' && summary.big_ideas && summary.big_ideas.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
              <Lightbulb className="w-6 h-6 text-yellow-500" />
              &ldquo;Brilliant&rdquo; Ideas
            </h2>
            
            <div className="space-y-3">
              {summary.big_ideas.map((idea, index) => (
                <DrunkCard 
                  key={index} 
                  variant="yellow" 
                  rotate 
                  className="slide-up p-4"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`idea-card-${index}`}
                >
                  <p className="font-body text-sm mb-2">&ldquo;{idea.content}&rdquo;</p>
                  <div className="flex items-center justify-between text-xs opacity-70">
                    <span className="font-bold">— {idea.author}</span>
                    <span className="font-mono">{idea.time}</span>
                  </div>
                </DrunkCard>
              ))}
            </div>
          </div>
        )}

        {/* Photo Gallery */}
        {summary.photos && summary.photos.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
              <Camera className={`w-6 h-6 ${theme.accent}`} />
              {theme.galleryTitle}
            </h2>
            
            <div className="grid grid-cols-2 gap-3">
              {summary.photos.map((photo, index) => (
                <div 
                  key={index}
                  className="relative group cursor-pointer"
                  onClick={() => setSelectedPhoto(photo)}
                  data-testid={`photo-gallery-${index}`}
                >
                  <div className="aspect-square overflow-hidden rounded-xl border-2 border-black shadow-brutal">
                    <img 
                      src={photo.url} 
                      alt={photo.caption || "Photo"}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white p-1.5 rounded-b-xl">
                    <p className="font-mono text-xs">{photo.time}</p>
                    <p className="font-bold text-xs truncate">{photo.author}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Photo Modal */}
        {selectedPhoto && (
          <div 
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
            onClick={() => setSelectedPhoto(null)}
          >
            <button 
              className="absolute top-4 right-4 text-white hover:text-pink"
              onClick={() => setSelectedPhoto(null)}
            >
              <X className="w-8 h-8" />
            </button>
            <div className="max-w-full" onClick={e => e.stopPropagation()}>
              <img 
                src={selectedPhoto.url} 
                alt={selectedPhoto.caption || "Photo"}
                className="max-w-full max-h-[70vh] object-contain rounded-xl border-4 border-white"
              />
              <div className="mt-3 text-center text-white">
                <p className="font-heading text-base">{selectedPhoto.caption}</p>
                <p className="font-body text-xs opacity-70">
                  📸 {selectedPhoto.author} {selectedPhoto.time && `at ${selectedPhoto.time}`}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Timeline */}
        {summary.timeline && summary.timeline.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
              <Clock className={`w-6 h-6 ${theme.accent}`} />
              The Journey
            </h2>
            
            <div className="space-y-4">
              {summary.timeline.map((item, index) => (
                <div 
                  key={index} 
                  className="timeline-item slide-up"
                  style={{ animationDelay: `${index * 0.1}s` }}
                  data-testid={`timeline-item-${index}`}
                >
                  <p className={`font-mono font-bold ${theme.accent} text-sm mb-2`}>{item.time}</p>
                  <div className="space-y-2">
                    {item.messages.map((msg, msgIndex) => (
                      <div key={msgIndex} className="bg-white p-2.5 rounded-lg border-2 border-black text-sm">
                        <span className="font-bold">{msg.sender}:</span>{' '}
                        <span className="font-body">{msg.content}</span>
                      </div>
                    ))}
                    {item.count > 3 && (
                      <p className="text-xs text-black/50 font-body">
                        +{item.count - 3} more...
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Participants */}
        <div className="mb-8">
          <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
            <Users className={`w-6 h-6 ${theme.accent}`} />
            {mode === 'keepsake' ? 'Guest Book' : mode === 'roadtrip' ? 'Travel Crew' : 'The Crew'}
          </h2>
          
          <div className="flex flex-wrap gap-2">
            {summary.participants?.map((name, index) => (
              <span 
                key={index}
                className="bg-white px-3 py-1.5 rounded-full border-2 border-black font-bold text-sm shadow-brutal"
                data-testid={`participant-${index}`}
              >
                {theme.participantEmoji} {name}
              </span>
            ))}
          </div>
        </div>

        {/* Coin Leaderboard - Only for modes that use it */}
        {summary.coin_leaderboard && summary.coin_leaderboard.length > 0 && (
          <div className="mb-8">
            <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2">
              <Trophy className="w-6 h-6 text-yellow-500" />
              {mode === 'keepsake' ? 'Most Generous' : 'Coin Leaderboard'} 🪙
            </h2>
            
            <div className="space-y-2">
              {summary.coin_leaderboard.map((entry, index) => (
                <DrunkCard 
                  key={index}
                  variant={index === 0 ? "yellow" : "default"}
                  className={`flex items-center justify-between p-3 ${index === 0 ? 'scale-[1.02]' : ''}`}
                  data-testid={`leaderboard-${index}`}
                >
                  <div className="flex items-center gap-2">
                    <span className="font-heading text-lg font-bold">
                      {index === 0 ? '👑' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                    </span>
                    <span className="font-heading text-base font-bold">{entry.name}</span>
                  </div>
                  <div className="flex items-center gap-1 bg-white px-3 py-1 rounded-full border-2 border-black">
                    <Coins className="w-4 h-4" />
                    <span className="font-mono font-bold">{entry.coins}</span>
                  </div>
                </DrunkCard>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center py-6">
          <p className="font-body text-black/50 text-xs mb-4">
            {mode === 'keepsake' 
              ? 'This keepsake is saved for 7 days 💝' 
              : 'Session expires 7 days after creation'}
          </p>
          
          <DrunkButton
            variant="primary"
            size="large"
            onClick={() => navigate('/')}
            data-testid="start-new-session-btn"
            className="w-full py-4"
          >
            {theme.footerText}
          </DrunkButton>
        </div>
      </div>
    </div>
  );
};

export default Summary;
